<template>
  <v-app>
    <Callback v-if="onCallbackPath" />
    <Unavailable v-else-if="session.status === 'unavailable'" />
    <ProgrammeInvite v-else-if="session.status === 'programme-invite'" />
    <FirmSetup v-else-if="session.status === 'needs-firm' || session.status === 'firm-guide'" />
    <Onboarding v-else-if="session.status === 'needs-org'" />
    <NoAccess v-else-if="session.status === 'no-access'" />
    <VerifyEmail v-else-if="session.status === 'verify-email'" />
    <AcceptTerms v-else-if="session.status === 'terms-required'" />
    <OrgPicker v-else-if="session.status === 'org-pick'" />
    <!-- PAC-1388: the Packhouse Edition's own sign-up page, on a front door
         that offers it, while signed out. Anywhere else /signup is the sign-in page. -->
    <CompanySignupPage v-else-if="showCompanySignup" />
    <!-- Better Auth's password-reset link lands here with ?token=. -->
    <ResetPassword v-else-if="showResetPassword" />
    <Login v-else-if="session.status === 'signed-out' || session.status === 'error'" />
    <template v-else-if="session.status === 'ready'">
      <TopBar />
      <Sidebar />
      <!-- INS-9: a failed module import used to leave this region empty.
           Chrome stays; the message fills the content area the module would
           have mounted into. AppHome is only at "/" — hosted modules mount
           onto document.body for every other path, so neither view competes
           with a loaded module for the same screen. -->
      <v-main v-if="moduleLoadKind">
        <ModuleLoadScreen :kind="moduleLoadKind" @retry="retryFailedModuleLoads" />
      </v-main>
      <v-main v-else-if="showAppHome">
        <AppHome />
      </v-main>
    </template>
    <!-- single-spa mounts the tenant's modules straight onto document.body
         (single-spa-vue's default target — no host container needed);
         modules pad themselves below via --metaship-header-height. -->
    <div v-else class="standalone-loading">
      <OrbitalLoader />
    </div>
    <!-- Outside the status branches on purpose: a parked update is worth
         offering on any screen, including the sign-in one. -->
    <UpdateBanner />
    <!-- BOE-51: the install offer — signed-in app + Boer's landing (it decides). -->
    <InstallBanner />
    <!-- Mounted ONCE, at app level, because it is opened from two places now:
         the account menu and a module's Settings (see shell/appInfoState.ts).
         It used to live inside SidebarFooter, i.e. inside the sidebar's own
         transformed <nav>, which is what squashed it to 229px. -->
    <AppInfoDrawer />
  </v-app>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, onUnmounted, ref, watch } from "vue";
import { useDisplay } from "vuetify";
import { useSessionStore } from "@/stores/session";
import { closeNav, rail } from "./navState";
import Sidebar from "./Sidebar.vue";
import OrbitalLoader from "./OrbitalLoader.vue";
import UpdateBanner from "./UpdateBanner.vue";
import InstallBanner from "./InstallBanner.vue";
import AppInfoDrawer from "./AppInfoDrawer.vue";
import { listenForAppInfoRequests } from "./appInfoState";
import { moduleOwnsScreen, syncModuleHost } from "./pageScroll";
import TopBar from "./TopBar.vue";
import AppHome from "./views/AppHome.vue";
import Login from "./views/Login.vue";
import Callback from "./views/Callback.vue";
import NoAccess from "./views/NoAccess.vue";
import Onboarding from "./views/Onboarding.vue";
import ProgrammeInvite from "./views/ProgrammeInvite.vue";
import FirmSetup from "./views/FirmSetup.vue";
import Unavailable from "./views/Unavailable.vue";
import OrgPicker from "./views/OrgPicker.vue";
import CompanySignupPage from "./views/CompanySignupPage.vue";
import VerifyEmail from "./views/VerifyEmail.vue";
import AcceptTerms from "./views/AcceptTerms.vue";
import ResetPassword from "./views/ResetPassword.vue";
import { RESET_PASSWORD_PATH } from "@/auth/identity";
import { isSignupPath, offersCompanySignup } from "@/signup/companySignup";
import ModuleLoadScreen from "./ModuleLoadScreen.vue";
import { moduleLoadKind, retryFailedModuleLoads } from "@/spa/moduleLoadFailure";

export default defineComponent({
  name: "App",
  components: {
    Sidebar,
    TopBar,
    AppHome,
    OrbitalLoader,
    UpdateBanner,
    InstallBanner,
    AppInfoDrawer,
    Login,
    Callback,
    NoAccess,
    Onboarding,
    ProgrammeInvite,
    FirmSetup,
    Unavailable,
    OrgPicker,
    CompanySignupPage,
    VerifyEmail,
    AcceptTerms,
    ResetPassword,
    ModuleLoadScreen,
  },
  setup() {
    const session = useSessionStore();
    const path = ref(window.location.pathname);
    // Callback rendering has two triggers, OR'd — both are load-bearing:
    // 1. `path === "/auth0/callback"` ALONE (any status): the return from
    //    Auth0 is a full-page load, so the pre-redirect "authenticating"
    //    status died with the old page — the fresh boot arrives here as
    //    "signed-out". Requiring an auth status here meant nobody ever
    //    exchanged the code (boer.metaship.ai first login, 2026-08-27: URL
    //    flipped to /auth0/callback?code=… and sat on the Login screen).
    // 2. `status === "authenticating"`: Callback.vue clears the URL before
    //    its async work, so `path` leaves "/auth0/callback" almost
    //    immediately; this keeps the spinner mounted through the code
    //    exchange. `completeAuth0Callback` sets it synchronously on its
    //    first line, so there is no gap between the two conditions.
    // Every terminal status the flow resolves to (ready/org-pick/no-access/
    // error) ends "authenticating" while the path is already "/", so the
    // spinner always exits — the stuck-spinner case this gate originally
    // guarded against stays covered.
    const onCallbackPath = computed(
      () => path.value === "/auth0/callback" || session.status === "authenticating",
    );

    // `mobileBreakpoint: 960` — plugins/vuetify.ts. The nav is an OVERLAY
    // below it (Sidebar.vue), so it occupies no layout width and every hosted
    // module gets the full viewport.
    const { mobile } = useDisplay();
    watch(
      [rail, mobile],
      ([collapsed, isMobile]) => {
        publishNavWidth(isMobile ? "0px" : collapsed ? "56px" : "230px");
        // Rotating a phone to a tablet width leaves the temporary drawer's
        // state behind; a permanent drawer is already showing, so a stale
        // "open" would only re-appear as an overlay on the way back.
        if (!isMobile) closeNav();
      },
      { immediate: true },
    );

    const showAppHome = computed(() => path.value === "/" && !!session.tenant?.homePage?.enabled);

    const showCompanySignup = computed(
      () =>
        isSignupPath(path.value) &&
        (session.status === "signed-out" || session.status === "error") &&
        offersCompanySignup(session.tenant),
    );

    const showResetPassword = computed(
      () => path.value === RESET_PASSWORD_PATH && (session.status === "signed-out" || session.status === "error"),
    );

    // PAC-1384: no page scroll while a hosted module owns the screen.
    watch(
      () => moduleOwnsScreen(session.status, showAppHome.value),
      (owns) => syncModuleHost(owns),
      { immediate: true },
    );

    function onRoutingEvent(): void {
      path.value = window.location.pathname;
    }

    // Re-read the URL on every status change. Callback.vue clears the callback
    // URL with `history.replaceState`, which fires NEITHER `popstate` NOR
    // `single-spa:before-routing-event` — single-spa only emits routing events
    // once `singleSpa.start()` has run, and start() waits for status "ready"
    // (main.ts). So during sign-in `path` stayed at "/auth0/callback" and
    // `onCallbackPath` never went false: a visitor whose account resolves to
    // the ORG PICKER sat on "Completing sign-in…" forever, and only a manual
    // refresh showed the picker (reported 2026-08-27). The single-org path hid
    // the bug — it ends in a navigateToUrl(home) that does fire the event.
    watch(() => session.status, onRoutingEvent);

    onMounted(() => {
      // A module lives in another bundle and cannot import the shell's state,
      // so it asks for App info by firing an event (shell/appInfoState.ts).
      listenForAppInfoRequests();
      document.documentElement.style.setProperty("--metaship-footer-height", "0px");
      // Matches TopBar's height exactly: Vuetify's compact app-bar (48px) plus
      // the iOS status-bar inset it pads by when the installed app runs
      // `black-translucent` (index.html STATUS-BAR CONTRACT; env() is 0
      // everywhere else) — the single source of truth every hosted module's
      // `padding-top: var(--metaship-header-height)` reads.
      document.documentElement.style.setProperty("--metaship-header-height", "calc(48px + env(safe-area-inset-top, 0px))");
      window.addEventListener("popstate", onRoutingEvent);
      window.addEventListener("single-spa:before-routing-event", onRoutingEvent);
    });
    onUnmounted(() => {
      syncModuleHost(false);
      window.removeEventListener("popstate", onRoutingEvent);
      window.removeEventListener("single-spa:before-routing-event", onRoutingEvent);
    });

    return {
      session,
      onCallbackPath,
      showAppHome,
      showCompanySignup,
      showResetPassword,
      moduleLoadKind,
      retryFailedModuleLoads,
    };
  },
});

/** The width every hosted module offsets itself by (Appendix A5). */
function publishNavWidth(width: string): void {
  document.documentElement.style.setProperty("--metaship-nav-width", width);
}
</script>
